import { connected } from '@ambiki/impulse';
import { toggleSet } from 'helpers/array';

const MESSAGE = 'You have unsaved changes. Are you sure you want to leave this page?';
const dirtyForms = new Set<HTMLFormElement>();

// Add the 'data-warn-unsaved-changes' attribute to any form element and it will be monitored for changes. If the user
// attempts to navigate away from the page without first saving their changes, an alert will warn them.
connected<HTMLFormElement>('form[data-warn-unsaved-changes]', (form) => {
  const controller = new AbortController();
  const { signal } = controller;
  const removeCurrent = () => dirtyForms.delete(form);

  form.addEventListener('input', prepareUnsavedChangesWarning, { signal });
  form.addEventListener('change', prepareUnsavedChangesWarning, { signal });
  form.addEventListener('mark-dirty', () => dirtyForms.add(form), { signal });
  form.addEventListener('submit', removeCurrent, { signal });

  return () => {
    controller.abort();
    removeCurrent();
  };
});

let latestClickTarget: EventTarget | null;

document.addEventListener(
  'click',
  (event) => {
    latestClickTarget = event.target;
  },
  true
);

document.addEventListener('turbolinks:before-visit', (event) => {
  // Show the confirm message only if the user is navigating away from the page by clicking an anchor element.
  // Otherwise, if there are multiple forms in the page, UX will deteriorate.
  const isLink = !!(latestClickTarget instanceof Element && latestClickTarget.closest('a[href]'));
  // eslint-disable-next-line no-alert
  if (isLink && hasDirtyForm() && !window.confirm(MESSAGE)) {
    event.preventDefault();
  }
});

window.addEventListener('beforeunload', (event) => {
  // This message is ignored by most browsers.
  if (hasDirtyForm()) {
    event.returnValue = MESSAGE;
  }
});

function prepareUnsavedChangesWarning(event: Event) {
  const currentTarget = event.currentTarget as HTMLFormElement;
  toggleSet(dirtyForms, currentTarget, hasDirtyFields(currentTarget));
}

function hasDirtyForm(): boolean {
  // Compare with the actual DOM because dirty form fields may not be present.
  return [...dirtyForms].some((form) => document.body.contains(form));
}

function hasDirtyFields(container: Element): boolean {
  const elements = Array.from(
    container.querySelectorAll(
      'input:not([data-warn-unsaved-changes-exclude]), textarea:not([data-warn-unsaved-changes-exclude])'
    )
  );
  for (const field of elements) {
    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
      if (formFieldValueChanged(field)) {
        return true;
      }
    }
  }
  return false;
}

// Support autocomplete once this is added: https://github.com/Ambiki/impulse_view_components/issues/50
function formFieldValueChanged(input: HTMLInputElement | HTMLTextAreaElement): boolean {
  if (input instanceof HTMLInputElement && (input.type === 'checkbox' || input.type === 'radio')) {
    return input.checked !== input.defaultChecked;
  }
  return input.value !== input.defaultValue;
}
